import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, X, Search, ShieldCheck, LinkIcon } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import Pagination from "../ui/pagination";

interface MappedProduct {
  id: string;
  code: string;
  name: string;
  mrp: string;
  pack: string;
  mfr: string;
  branch_id: number;
  tp_code: string;
  med_products_id: number;
  // Mapped product details
  mapped_product_name?: string;
  mapped_sku?: string;
  mapped_brand?: string;
  mapped_manufacturer?: string;
  mapped_form?: string;
  mapped_item_size?: string;
  mapped_unit_type?: string;
  mapped_pack_form?: string;
  mapped_date?: string;
  is_auto_mapped?: boolean;
  mapped_master_price?: string;
  mapped_by?: string;
  mapped_by_name?: string;
  mapped_by_email?: string;
}

interface MappedB2CProductsProps {
  entities: any;
  selectedEntity?: any;
  onEntityChange?: (entity: any) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  showOnlyOtc?: boolean;
  showMrpZero?: boolean;
  onStatisticsUpdate?: () => void;
}

const MappedB2CProducts = ({
  entities,
  selectedEntity: parentSelectedEntity,
  onEntityChange,
  page: parentPage,
  onPageChange,
  showOnlyOtc,
  showMrpZero,
  onStatisticsUpdate
}: MappedB2CProductsProps) => {
  const [products, setProducts] = useState<MappedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(parentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState({ mapped: 0, unmapped: 0 });
  const [selectedEntity, setSelectedEntity] = useState<any>(parentSelectedEntity || null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMRPZero, setShowMRPZero] = useState(showMrpZero || false);

  const handleUnmapProduct = async (productId: string) => {
    if (!window.confirm("Are you sure you want to unmap this product? It will be moved back to unmapped products.")) {
      return;
    }

    try {
      const response = await api.removeProductLink(productId);

      if (response.success) {
        toast.success("Product unmapped successfully");
        fetchMappedProducts();
        // Refresh statistics to update counts with a small delay to ensure DB transaction completes
        if (onStatisticsUpdate) {
          setTimeout(() => {
            onStatisticsUpdate();
          }, 500);
        }
      } else {
        toast.error(response.message || "Failed to unmap product");
      }
    } catch (error) {
      toast.error("Error unmapping product");
    }
  };

  const fetchMappedProducts = useCallback(async () => {
    // Allow null entity to show all products
    setLoading(true);
    try {
      const response = await api.getB2CMappedProducts(
        page,
        20,
        "mapped",
        selectedEntity,
        showOnlyOtc || false,
        searchQuery,
        showMRPZero
      );
      if (response.success) {
        setProducts(response.data);
        setTotalPages(response.pagination.totalPages);
        if (response.counts) {
          setTotalCount(response.counts);
        }
      } else {
        toast.error("Failed to fetch mapped products");
      }
    } catch (error) {
      toast.error("Error fetching mapped products");
    } finally {
      setLoading(false);
    }
  }, [page, selectedEntity, showOnlyOtc, searchQuery, showMRPZero]);

  useEffect(() => {
    if (parentSelectedEntity !== undefined) {
      setSelectedEntity(parentSelectedEntity);
    }
  }, [parentSelectedEntity]);

  useEffect(() => {
    if (parentPage !== undefined) {
      setPage(parentPage);
    }
  }, [parentPage]);

  useEffect(() => {
    if (showMrpZero !== undefined) {
      setShowMRPZero(showMrpZero);
    }
  }, [showMrpZero]);

  useEffect(() => {
    // Always fetch products (null entity means all products)
    fetchMappedProducts();
  }, [fetchMappedProducts]);

  const handleEntityChange = (entity: any) => {
    setSelectedEntity(entity);
    if (onEntityChange) {
      onEntityChange(entity);
    }
    setPage(1);
    if (onPageChange) {
      onPageChange(1);
    }
    setSearchQuery("");
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  return (
    <>
      <div className="">
        {/* Search Bar */}
        <div className="px-6 py-3 bg-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search products by name or code..."
                className="w-full pl-10 pr-10 py-2.5 bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {searchQuery && (
              <Button
                variant="outline"
                onClick={handleClearSearch}
                className="border-gray-500 text-gray-300 hover:bg-gray-600 bg-gray-700"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64 bg-gray-800">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-700 border-b border-gray-600">
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Product Code</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Product Name</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">MRP</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Packing</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Manufacturer</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                      <TableCell className="py-4 px-6">
                        <div className="text-gray-300 font-mono">{product.code}</div>
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="text-gray-100 text-sm">{product.name}</div>
                          {product.mapped_product_name && (
                            <div className="text-green-400 text-sm font-medium flex items-center gap-2">
                              {product.mapped_product_name}
                              {product.is_auto_mapped && (
                                <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs px-1 py-0">
                                  auto
                                </Badge>
                              )}
                            </div>
                          )}
                          {(product.mapped_by_name || product.mapped_date) && (
                            <div className="text-xs text-gray-400 mt-1">
                              Linked: {product.mapped_date ? new Date(product.mapped_date).toLocaleDateString() : 'N/A'}
                              {product.mapped_by_name && ` by ${product.mapped_by_name}`}
                              {product.mapped_by_email && ` (${product.mapped_by_email})`}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        {product.mapped_master_price ? (
                          <div className="space-y-1">
                            <div className="text-gray-100 text-sm">₹{product.mrp}</div>
                            <div className="text-green-400 text-sm font-medium">₹{product.mapped_master_price}</div>
                          </div>
                        ) : (
                          <div className="text-gray-100">₹{product.mrp}</div>
                        )}
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        {product.mapped_form ? (
                          <div className="space-y-1">
                            <div className="text-gray-300 text-sm">{product.pack}</div>
                            <div className="text-green-400 text-sm">
                              {(() => {
                                // Construct packing: item_size unit_type of drug_form in pack_form
                                const parts = [];
                                if (product.mapped_item_size) parts.push(product.mapped_item_size);
                                if (product.mapped_unit_type) parts.push(product.mapped_unit_type);
                                if (product.mapped_form) {
                                  if (parts.length > 0) {
                                    parts.push('of', product.mapped_form);
                                  } else {
                                    parts.push(product.mapped_form);
                                  }
                                }
                                if (product.mapped_pack_form) {
                                  if (parts.length > 0) {
                                    parts.push('in', product.mapped_pack_form);
                                  } else {
                                    parts.push(product.mapped_pack_form);
                                  }
                                }
                                return parts.join(' ') || product.mapped_form || 'N/A';
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-300">{product.pack}</div>
                        )}
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        {product.mapped_manufacturer ? (
                          <div className="space-y-1">
                            <div className="text-gray-300 text-sm">{product.mfr}</div>
                            <div className="text-green-400 text-sm">{product.mapped_manufacturer}</div>
                            {product.mapped_brand && product.mapped_brand !== product.mapped_manufacturer && (
                              <div className="text-blue-400 text-xs">Brand: {product.mapped_brand}</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-300">{product.mfr}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-center py-3 px-6">
                        <Button
                          size="sm"
                          onClick={() => handleUnmapProduct(product.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center gap-2 mx-auto"
                        >
                          <X className="w-4 h-4" />
                          Unmap
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {products.length === 0 && (
              <div className="text-center py-8 text-gray-400 bg-gray-800">
                {searchQuery ? "No products found matching your search" : "No mapped products found"}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center py-4 bg-gray-700 border-t border-gray-600">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default MappedB2CProducts;